import { IAppComponentProps } from '../../../components';
import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, DatePicker, Form, Modal, Select, Tooltip } from 'antd';
import { PortalFormItem } from '../../../components/FormUtils';
import { useIntl } from 'react-intl';
import { BmwCompanyCode } from '../../../components/BmwCompanyCode';
import { useLazyQuery } from '@apollo/client';
import { getI18n, isExternalUser } from '../../../utils/Utils';
import { getUserEntitlementResultByUserIdAndFilter_getUserEntitlementResultByUserIdAndFilter } from '../../../main/__generated__/getUserEntitlementResultByUserIdAndFilter';
import { updateSelectedPartner } from '../IStoredPartnerRecordSlice';
import { useAppDispatch } from '../../../main/hooks';
import { filteredEntitlementLookupForSelf } from '../../../main/entitlementService';
import { getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners } from '../../../main/__generated__/getSelfEntitlementResultByFilter';
import { DOCUMENT_USER_ENTITLEMENT_RESULT_BY_USER_ID } from '../../../main/queries';
import { getDateFormatOrDefault } from '../../users/UserFormatPreferenceServices';
import dayjs from 'dayjs';
import { getDocumentUserEntitlementResultByUserId } from '../../../main/__generated__/getDocumentUserEntitlementResultByUserId';
import styles from '../styling/ecap.module.css';
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';

type DraftDocumentSearchFormProps = {
    onSearch: any;
} & IAppComponentProps;

const draft_Entitlement = 'create_dp@ecap';

const DraftDocumentSearchForm: React.FunctionComponent<DraftDocumentSearchFormProps> = (props) => {
    const [checked, setChecked] = React.useState(false);

    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [selectOptions, setSelectOption] = useState<
        getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners[]
    >([]);
    const { Option } = Select;
    const [filteredEntitlementDetail, setFilteredEntitlementDetail] =
        useState<getUserEntitlementResultByUserIdAndFilter_getUserEntitlementResultByUserIdAndFilter>();
    const [loading, isLoading] = useState<boolean>(false);
    const [partnerId, setPartnerId] = useState<number>(undefined);
    const intl = useIntl();

    const removeDuplicated = (data: getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners[]) =>
        Array.from(
            data
                .reduce((acc, item) => (item && item['partnerId'] && acc.set(item['partnerId'], item), acc), new Map())
                .values()
        );

    const searchPartner = (partnerName) => {
        Promise.resolve(filteredEntitlementLookupForSelf(draft_Entitlement, partnerName, 1, 200))
            .then((response) => {
                const searchOption = removeDuplicated(response.getSelfEntitlementResultByFilter.partners);
                setSelectOption(searchOption);
                isLoading(false);
            })
            .catch((err) => {
                console.error(err);
                isLoading(false);
            });
    };

    const findSelectedPartner = (partnerId) => {
        return selectOptions.find((item) => {
            if (item.partnerId === partnerId) {
                return item;
            }
        });
    };
    const handleSearch = (value: string) => {
        isLoading(true);
        searchPartner(value);
    };
    const [getUserEntitlement, {}] = useLazyQuery<getDocumentUserEntitlementResultByUserId>(
        DOCUMENT_USER_ENTITLEMENT_RESULT_BY_USER_ID,
        {
            variables: {
                userId: props.currentUser?.uid,
                entitlement: 'create_dp@ecap',
                partnerName: String,
                authentication: localStorage.getItem('id_token'),
            },
            fetchPolicy: 'network-only',
        },
    );

    const checkPartnerEntitlement = () => {
        if (isExternalUser(props.distinctEntitlements)) {
            isLoading(true);
            getUserEntitlement().then((response) => {
                const userEntitlement = response.data?.getDocumentUserEntitlementResultByUserId;
                if (userEntitlement?.partners?.length === 1) {
                    form.setFieldsValue({
                        partnerId: `${userEntitlement.partners[0]?.partnerNo} - ${userEntitlement.partners[0]?.partnerName}`,
                    });
                    setPartnerId(userEntitlement.partners[0]?.partnerId);
                }
                if (userEntitlement?.partners?.length > 1) {
                    setSelectOption(userEntitlement?.partners);
                }
                isLoading(false);
            });
        }
    };

    useEffect(() => {
        checkPartnerEntitlement();
    }, []);

    const onSearch = () => {
        if (partnerId) {
            const values = { ...form.getFieldsValue(), partnerId };
            props.onSearch(values);
        } else {
            props.onSearch(form.getFieldsValue());
        }
    };

    const onClear = () => {
        Modal.confirm({
            title: intl.formatMessage({
                id: 'e-cap-draft',
                defaultMessage: 'Reset Input Data',
            }),
            content: intl.formatMessage({
                id: 'e-cap-message',
                defaultMessage: 'Input Data will be lost. Do you want to continue?',
            }),
            okText: intl.formatMessage({
                id: 'e-cap-capture-document-cancel-confirm-modal-yes',
                defaultMessage: 'YES',
            }),
            cancelText: intl.formatMessage({
                id: 'e-cap-capture-document-cancel-confirm-modal-no',
                defaultMessage: 'NO',
            }),
            onOk: () => form.resetFields(),
        });
    };

    const handleChange = (value) => {
        isLoading(true);
        dispatch(updateSelectedPartner(findSelectedPartner(value)));
        setChecked(!checked);
        console.log(!checked);
    };
    const onFinishFailed = (errorInfo: any) => {
        console.log('Failed:', errorInfo);
    };

    return (
        <>
            <Form
                size={'middle'}
                form={form}
                style={{ width: '2100px' }}
                layout={'vertical'}
                onFinish={onSearch}
                onFinishFailed={onFinishFailed}
                initialValues={{ documentDateFrom: dayjs(), documentDateTo: dayjs() }}
            >
                <Card title={getI18n('management-searchmask-title', 'Search Mask', intl)} size={'default'}>
                    <div className={styles.draftDocumentFirstRow}>
                        <div>
                            <label>{getI18n('welcome-From Document Date', 'From Document Date', intl)}</label>
                            <Form.Item
                                name={'documentDateFrom'}
                                key={'documentDateFrom'}
                                rules={[
                                    {
                                        required: true,
                                        message: intl.formatMessage({
                                            id: 'e-cap-capture-document-select-reception-date-from',
                                            defaultMessage: 'Please Select Reception Date From',
                                        }),
                                    },
                                ]}
                            >
                                <DatePicker
                                    defaultValue={dayjs()}
                                    format={getDateFormatOrDefault(props.currentUser)}
                                    placeholder={getI18n('welcome-From Document Date', 'From Document Date', intl)}
                                    style={{ width: 200 }}
                                    allowClear={true}
                                    data-testid={'documentDateFrom'}
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    disabledDate={(current) => current.isAfter(dayjs().subtract(0, 'day'))}
                                />
                            </Form.Item>
                        </div>

                        <div>
                            <label>{getI18n('welcome-To Document Date', 'To Document Date', intl)}</label>
                            <Form.Item
                                name={'documentDateTo'}
                                key={'documentDateTo'}
                                rules={[{ required: true, message: 'Please Select Document Date To' }]}
                            >
                                <DatePicker
                                    defaultValue={dayjs()}
                                    format={getDateFormatOrDefault(props.currentUser)}
                                    placeholder={getI18n('welcome-To Document Date', 'To Document Date', intl)}
                                    style={{ width: 200 }}
                                    allowClear={true}
                                    data-testid={'documentDateTo'}
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    disabledDate={(current) => current.isAfter(dayjs().subtract(0, 'day'))}
                                />
                            </Form.Item>
                        </div>

                        <div>
                            <label>{getI18n('partner-overview', 'Partner', intl)}</label>
                            <Form.Item name={'partnerId'}>
                                <Select
                                    size={'middle'}
                                    showSearch={true}
                                    placeholder={getI18n(
                                        'document-overview-search-message',
                                        'Enter partner number or partner name',
                                        intl
                                    )}
                                    onSearch={handleSearch}
                                    onChange={handleChange}
                                    defaultActiveFirstOption={true}
                                    filterOption={false}
                                    notFoundContent={null}
                                    loading={loading}
                                >
                                    {selectOptions &&
                                        selectOptions.map((item) => (
                                            <Select.Option
                                                data-testid={`partnerId_${item.partnerNo}`}
                                                key={`${item.partnerId}_${item.partnerNo}`}
                                                value={item.partnerId}
                                            >
                                                {item.partnerNo} - {item.partnerName}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </div>
                    </div>

                    <div className={styles.draftDocumenSecondRow}>
                        <div>
                            <PortalFormItem
                                label={getI18n('welcome-To-document-number', 'Document Numbers', intl)}
                                name={'documentNumbers'}
                                placeHolder={getI18n(
                                    'welcome-To-document-number-placeholder',
                                    'Enter Document Numbers, separate then with commas',
                                    intl
                                )}
                            />
                        </div>

                        <div>
                            <BmwCompanyCode
                                currentUser={props.currentUser}
                                intl={props.intl}
                                distinctEntitlements={props.distinctEntitlements}
                            />
                        </div>
                    </div>

                   <div>
                       <Form.Item name={'myDocuments'} valuePropName="checked" style={{height:"30px"}}>
                           <Checkbox className="mb25 mt35">
                               {getI18n('welcome-To-My Draft Documents', 'My Draft Documents  ', intl)}
                           </Checkbox>
                       </Form.Item>
                   </div>
                </Card>
                <Card size="small">
                    <div>
                        <Tooltip title={getI18n('welcome-search-btton', 'SEARCH', intl)}>
                            <Button
                                style={{marginRight:"10px"}}
                                type={'primary'}
                                htmlType="submit"
                                size="large"
                                className="w-100 mt10"
                                data-testid={'btn-search'}
                                icon={<SearchOutlined/>}
                            />
                        </Tooltip>

                        <Tooltip title={getI18n('welcome-Clear-button', 'CLEAR', intl)}>
                            <Button
                                style={{marginRight:"10px"}}
                                type="primary"
                                onClick={onClear}
                                size="large"
                                className="w-100 mt10"
                                data-testid={'btn-clear'}
                                icon={<ReloadOutlined/>}
                            />
                        </Tooltip>

                    </div>
                </Card>
            </Form>
        </>
    );
};
export default DraftDocumentSearchForm;
